import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { 
  AdventureHeader,
  AdventureScene,
  AdventureItem,
  AdventureStat,
  Player, 
  GameSession, 
  GameStatus,
  ApiError,
  UserInputActionResponse
} from '../types';
import { CustomStyle, SceneSection, RichTextDescription, RichTextElement } from 'coop-adventure-engine';
import apiService from '../services/api';
import useAuthStore from './authStore';
import guestService from '../services/guestService';
import authService from '../services/auth';

type UserVariableScope = 'scene' | 'character' | 'player' | 'adventure' | 'user';

interface UserVariableValueState {
  value: string;
  scope?: UserVariableScope;
}

type UserVariableMap = Record<string, UserVariableValueState>;

const createVariableValueState = (
  value: string,
  scope?: UserVariableScope,
  fallbackScope?: UserVariableScope
): UserVariableValueState => {
  const entry: UserVariableValueState = { value };
  const resolvedScope = scope ?? fallbackScope;
  if (resolvedScope !== undefined) {
    entry.scope = resolvedScope;
  }
  return entry;
};

interface WaitingCondition {
  playerId: string;
  optionId: string;
  message: string;
  waitingFor?: string[];
}

const isRichTextElement = (value: unknown): value is RichTextElement =>
  typeof value === 'object' && value !== null && 'type' in (value as Record<string, unknown>);

// Coalesce concurrent loadCurrentScene calls for the same session+player (single in-flight request)
let loadCurrentScenePromise: Promise<void> | null = null;
let loadCurrentSceneKey: string | null = null;

const applyUserVariableToElement = (
  element: RichTextElement,
  variableMap: UserVariableMap
): { updated: RichTextElement; mutated: boolean } => {
  let mutated = false;
  let updatedElement = element;

  if (element.type === 'user-input') {
    const variableName = element.userInputAttributes?.variable;
    if (variableName) {
      const targetValue = variableMap[variableName]?.value ?? '';
      const currentValue =
        typeof element.content === 'string'
          ? element.content
          : Array.isArray(element.content)
            ? ''
            : '';

      if (currentValue !== targetValue) {
        updatedElement = {
          ...element,
          content: targetValue
        };
        mutated = true;
      }
    }
  }

  if (Array.isArray(element.content) && element.content.every(isRichTextElement)) {
    const originalChildren = element.content as RichTextElement[];
    let childrenMutated = false;

    const updatedChildren = originalChildren.map((child) => {
      const { updated, mutated: childMutated } = applyUserVariableToElement(child, variableMap);
      if (childMutated) {
        childrenMutated = true;
      }
      return updated;
    });

    if (childrenMutated) {
      updatedElement = mutated
        ? { ...updatedElement, content: updatedChildren }
        : { ...element, content: updatedChildren };
      mutated = true;
    }
  }

  return { updated: updatedElement, mutated };
};

const updateRichTextDescriptionWithVariables = (
  description: string | RichTextDescription,
  variableMap: UserVariableMap
): string | RichTextDescription => {
  if (!description || typeof description === 'string') {
    return description;
  }

  let mutated = false;
  const updatedElements = description.elements.map((element) => {
    const { updated, mutated: elementMutated } = applyUserVariableToElement(element, variableMap);
    if (elementMutated) {
      mutated = true;
    }
    return updated;
  });

  if (!mutated) {
    return description;
  }

  return {
    ...description,
    elements: updatedElements
  };
};

const updateSceneSectionsWithVariables = (
  sections: SceneSection[] | undefined,
  variableMap: UserVariableMap
): SceneSection[] | undefined => {
  if (!sections || sections.length === 0) {
    return sections;
  }

  let mutated = false;
  const updatedSections = sections.map((section) => {
    let sectionMutated = false;
    let updatedDescription: string | RichTextDescription | undefined = section.description;

    if (section.description) {
      const nextDescription = updateRichTextDescriptionWithVariables(section.description, variableMap);
      if (nextDescription !== section.description) {
        sectionMutated = true;
        mutated = true;
        updatedDescription = nextDescription as string | RichTextDescription;
      }
    }

    return sectionMutated
      ? {
          ...section,
          description: (updatedDescription ?? section.description) as string | RichTextDescription
        }
      : section;
  });

  return mutated ? (updatedSections as SceneSection[]) : sections;
};

const updateSceneWithVariables = (
  scene: { id: string; sections: SceneSection[]; style?: CustomStyle } | null,
  variableMap: UserVariableMap
) => {
  if (!scene) {
    return scene;
  }

  const updatedSections = updateSceneSectionsWithVariables(scene.sections, variableMap);
  if (updatedSections === scene.sections) {
    return scene;
  }

  return {
    ...scene,
    sections: updatedSections ?? []
  };
};

const collectUserInputValuesFromElement = (
  element: RichTextElement,
  accumulator: UserVariableMap
) => {
  if (!element) {
    return;
  }

  if (element.type === 'user-input') {
    const variableName = element.userInputAttributes?.variable;
    if (variableName) {
      const contentValue =
        typeof element.content === 'string'
          ? element.content
          : Array.isArray(element.content)
            ? ''
            : '';
      const previousScope = accumulator[variableName]?.scope;
      accumulator[variableName] = createVariableValueState(contentValue, undefined, previousScope);
    }
  }

  if (Array.isArray(element.content)) {
    for (const child of element.content) {
      if (isRichTextElement(child)) {
        collectUserInputValuesFromElement(child, accumulator);
      }
    }
  }
};

const extractUserInputValuesFromDescription = (
  description: string | RichTextDescription | undefined
): UserVariableMap => {
  if (!description || typeof description === 'string') {
    return {};
  }

  const accumulator: UserVariableMap = {};
  description.elements.forEach((element) => collectUserInputValuesFromElement(element, accumulator));
  return accumulator;
};

const extractUserInputValuesFromSections = (sections: SceneSection[] | undefined): UserVariableMap => {
  if (!sections || sections.length === 0) {
    return {};
  }

  return sections.reduce<UserVariableMap>((accumulator, section) => {
    const extracted = extractUserInputValuesFromDescription(section.description);
    return mergeUserVariableMaps(accumulator, extracted);
  }, {});
};

const mergeUserVariableMaps = (
  base: UserVariableMap,
  updates: UserVariableMap
): UserVariableMap => {
  if (!updates || Object.keys(updates).length === 0) {
    return base;
  }

  const merged: UserVariableMap = { ...base };
  Object.entries(updates).forEach(([variableName, valueState]) => {
    const previous = merged[variableName];
    // Preserve existing non-empty values when new value is empty (from scene definition defaults)
    // This ensures form fields retain their values when navigating back after errors
    const newValue = valueState.value ?? '';
    const existingValue = previous?.value ?? '';
    const finalValue = (newValue === '' && existingValue !== '') ? existingValue : newValue;
    
    const entry = createVariableValueState(
      finalValue,
      valueState.scope,
      previous?.scope
    );
    merged[variableName] = entry;
  });
  return merged;
};

const buildOptionsMapFromSections = (sections: SceneSection[] = []) => {
  const optionsMap: Record<string, any> = {};
  sections.forEach((section) => {
    const options = (section as any).options ?? [];
    options.forEach((option: any) => {
      if (option?.id) {
        optionsMap[option.id] = option;
      }
    });
  });
  return optionsMap;
};

const prepareSceneState = (
  scene: { id: string; sections: SceneSection[]; style?: CustomStyle } | null,
  availableOptionIds: string[] = [],
  waitingOptionIds: string[] = [],
  existingVariables: UserVariableMap = {}
) => {
  if (!scene) {
    return {
      scene: null,
      availableOptionIds: [],
      waitingOptionIds: [],
      optionsMap: {},
      userVariableValues: existingVariables
    };
  }

  const extractedVariables = extractUserInputValuesFromSections(scene.sections);
  const mergedVariables = mergeUserVariableMaps(existingVariables, extractedVariables);
  const normalizedScene = updateSceneWithVariables(scene, mergedVariables);
  const optionsMap = buildOptionsMapFromSections(normalizedScene?.sections ?? []);

  return {
    scene: normalizedScene,
    availableOptionIds,
    waitingOptionIds,
    optionsMap,
    userVariableValues: mergedVariables
  };
};

interface GameState {
  // Current session
  currentSession: GameSession | null;
  currentPlayer: any | null;
  
  // Adventure data (header includes style and character info)
  currentAdventure: AdventureHeader | null;
  
  // Adventure selection state
  availableAdventures: AdventureHeader[];
  adventureIds: string[];
  selectedAdventureId: string | null;
  selectedAdventure: AdventureHeader | null;
  isLoadingAdventures: boolean;
  adventureError: string | null;
  
  // Session lobby state
  sessionPlayers: Player[];
  sessionStatus: 'waiting' | 'active' | 'completed' | 'paused';
  isCreatingSession: boolean;
  sessionError: string | null;
  currentScene: {
    id: string;
    sections: SceneSection[];
    style?: CustomStyle;
  } | null;
  availableOptionIds: string[];
  waitingOptionIds: string[];
  optionsMap: Record<string, {
    id: string;
    text: string;
    target?: string;
    condition?: string;
    effect?: string;
    waitMessage?: string;
    autoTransition?: boolean;
  }>;
  
  // Player state
  players: any[];
  playerStates: Record<string, any>;
  
  // Location inventory state
  locationInventories: Record<string, string[]>; // locationTag -> itemTags[]
  
  // Real-time updates
  isConnected: boolean;
  waitingConditions: WaitingCondition[];
  reconnectAttempts: number;
  userVariableValues: UserVariableMap;
  pendingUserInputs: Record<string, { value: string; maxLength?: number }>;
  
  // Loading and error states
  isLoading: boolean;
  error: ApiError | null;

  // Config from backend (set when health/config is loaded)
  systemAdventureId: string;

  /** True while START_ADVENTURE is in progress so URL-sync effect navigates instead of clearing (survives Strict Mode remount) */
  startAdventureTransitionInProgress: boolean;

  // Actions
  setSystemAdventureId: (id: string) => void;
  setStartAdventureTransitionInProgress: (value: boolean) => void;
  joinSession: (sessionId: string) => Promise<void>;
  leaveSession: () => void;
  selectOption: (optionId: string) => Promise<boolean>; // Returns true if player completed
  selectFinishOption: () => Promise<void>;
  updatePlayerState: (updates: any) => void;
  setCurrentScene: (scene: { 
    id: string; 
    sections: SceneSection[];
    style?: CustomStyle;
  }) => void;
  updateGameState: (state: any) => void;
  setConnectionStatus: (isConnected: boolean) => void;
  addWaitingCondition: (condition: WaitingCondition) => void;
  removeWaitingCondition: (playerId: string, optionId: string) => void;
  setReconnectAttempts: (attempts: number) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  setUserVariableValue: (variableName: string, value: string, scope?: UserVariableScope) => void;
  setUserVariableValues: (values: UserVariableMap) => void;
  setPendingUserInput: (
    variableName: string,
    value: string,
    options?: { maxLength?: number }
  ) => void;
  clearPendingUserInput: (variableName: string) => void;
  flushPendingUserInputs: () => Promise<void>;
  
  // Adventure selection actions
  loadAdventures: () => Promise<void>;
  selectAdventure: (adventureId: string) => void;
  clearAdventureError: () => void;
  
  // Adventure management
  loadAdventure: (adventureId: string) => Promise<void>;
  loadAdventureHeader: (adventureId: string) => Promise<void>;
  loadAdventureScene: (adventureId: string, sceneId: string) => Promise<AdventureScene>;
  loadAdventureItem: (adventureId: string, itemName: string) => Promise<AdventureItem>;
  loadAdventureStat: (adventureId: string, statName: string) => Promise<AdventureStat>;
  createSession: (adventureId: string) => Promise<{ sessionId: string; playerId?: string }>;
  createGuestSession: (
    adventureId: string,
    invitePayload?: { variables?: Record<string, string> }
  ) => Promise<{ sessionId: string; playerId?: string }>;
  startSession: () => Promise<void>;
  loadCurrentScene: () => Promise<void>;
  
  // Load session state when player already exists (e.g., after joining via invite)
  loadSessionState: (sessionId: string, playerId: string, playerData?: any) => Promise<void>;
  
  // Session lobby actions
  joinSessionByCode: (sessionCode: string) => Promise<void>;
  clearSessionError: () => void;
  loadSessionPlayers: (sessionId: string) => Promise<void>;
  
  // Session management actions
  deleteSession: (sessionId: string) => Promise<void>;
  leaveSessionById: (sessionId: string) => Promise<void>;
  submitUserInput: (
    variableName: string,
    value: string,
    options?: { maxLength?: number }
  ) => Promise<UserInputActionResponse>;
  
  // Location inventory actions
  updateLocationInventory: (locationTag: string, items: string[]) => void;
  addItemToLocation: (locationTag: string, itemTag: string) => void;
  removeItemFromLocation: (locationTag: string, itemTag: string) => void;
  getLocationInventory: (locationTag: string) => string[];
  
  // Helper actions
  loadSystemSession: () => Promise<{ sessionId: string; playerId: string }>;
  clearSessionState: () => void;
}

const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentSession: null,
      currentPlayer: null,
      currentAdventure: null,
      
      // Adventure selection state
      availableAdventures: [],
      adventureIds: [],
      selectedAdventureId: null,
      selectedAdventure: null,
      isLoadingAdventures: false,
      adventureError: null,
      
      // Session lobby state
      sessionPlayers: [],
      sessionStatus: 'waiting',
      isCreatingSession: false,
      sessionError: null,
      
      currentScene: null,
      availableOptionIds: [],
      waitingOptionIds: [],
      optionsMap: {},
      players: [],
      playerStates: {},
      locationInventories: {},
      isConnected: false,
      waitingConditions: [],
      reconnectAttempts: 0,
      userVariableValues: {},
      pendingUserInputs: {},
      isLoading: false,
      error: null,
      systemAdventureId: 'storynity',
      startAdventureTransitionInProgress: false,

      setSystemAdventureId: (id: string) => set({ systemAdventureId: id }),
      setStartAdventureTransitionInProgress: (value: boolean) => set({ startAdventureTransitionInProgress: value }),

      // Actions
      joinSession: async (sessionId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // Clear previous session state before joining new session
          // This ensures clean state when switching between sessions
          get().clearSessionState();
          
          // Get session and players first to determine available playerSlot
          const session = await apiService.getSession(sessionId);
          const players = await apiService.getSessionPlayers(sessionId);
          
          // Get adventure to find available player slots
          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch (adventureError) {
              console.warn('Failed to load adventure details:', adventureError);
            }
          }
          
          // Determine available playerSlot
          let playerSlot = '';
          if (adventure && adventure.players) {
            // Find the first player slot that's not already taken
            // Players may have playerName field - check both the field name and ensure we're comparing correctly
            const takenSlots = new Set(
              players
                .map(p => {
                  // Try different possible field names
                  return (p as any).playerName || (p as any).player_name || (p as any).playerName;
                })
                .filter(Boolean)
            );
            
            // Find first available slot from adventure definition
            const availableSlot = adventure.players.find(p => {
              const slotName = p.playerName;
              return slotName && !takenSlots.has(slotName);
            });
            
            if (availableSlot) {
              playerSlot = availableSlot.playerName;
            } else {
              // If no slot found, it means all slots are taken or there's a mismatch
              console.error('[joinSession] No available slot found:', {
                adventurePlayers: adventure.players.map(p => p.playerName),
                takenSlots: Array.from(takenSlots),
                playersData: players.map(p => ({ id: p.id, playerName: (p as any).playerName, allFields: Object.keys(p) }))
              });
              throw new Error('No available player slots in this session');
            }
          } else {
            throw new Error('Unable to determine available player slot - adventure not loaded');
          }
          
          const player = await apiService.joinSession(sessionId, playerSlot);
          
          // Adventure already loaded above for playerSlot determination
          set({
            currentSession: session,
            currentPlayer: player,
            currentAdventure: adventure, // Set currentAdventure for useAdventureStyle
            sessionPlayers: players,
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: session.adventureId || null,
            isLoading: false,
            error: null,
          });
          
          // Load current scene and options
          await get().loadCurrentScene();
          
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      loadSessionState: async (sessionId: string, playerId: string, playerData?: any) => {
        if (import.meta.env.DEV) {
          console.log('[GameStore] loadSessionState: start', { sessionId, playerId });
        }
        set({ isLoading: true, error: null });
        
        try {
          // Clear previous session state
          get().clearSessionState();
          if (import.meta.env.DEV) {
            console.log('[GameStore] loadSessionState: cleared previous state');
          }

          // Load session
          const session = await apiService.getSession(sessionId);
          
          // Always load the session players list (needed for sessionPlayers state)
          // Use provided player data if available, otherwise look it up from the players list
          let player;
          let players: any[] = [];
          
          // Load players list (needed for sessionPlayers even if we have playerData)
          // Retry logic: the player might have just been created and not yet visible
          let retryCount = 0;
          const maxRetries = 5;
          while (retryCount < maxRetries) {
            players = await apiService.getSessionPlayers(sessionId);
            
            // If playerData was provided, use it; otherwise find player in the list
            if (playerData) {
              player = playerData;
              // Verify the player exists in the loaded players list
              const playerInList = players.find(p => p.id === playerId);
              if (playerInList) {
                break;
              } else {
                if (retryCount < maxRetries - 1) {
                  await new Promise(resolve => setTimeout(resolve, 300));
                  retryCount++;
                  continue;
                } else {
                  // Player not in list but we have playerData - use it anyway
                  console.warn('[GameStore] loadSessionState: Player not in list after retries, using provided data', { playerId });
                  break;
                }
              }
            } else {
              // Find player in the loaded list
              player = players.find(p => p.id === playerId);
              
              if (player) {
                break;
              }
              
              if (retryCount < maxRetries - 1) {
                await new Promise(resolve => setTimeout(resolve, 300));
                retryCount++;
              } else {
                console.error('[GameStore] loadSessionState: Player not found after retries', { playerId, sessionId });
                throw new Error(`Player ${playerId} not found in session ${sessionId} after ${maxRetries} attempts`);
              }
            }
          }
          
          if (!player) {
            console.error('[GameStore] loadSessionState: Player not found or could not be loaded', { playerId, sessionId });
            throw new Error(`Player ${playerId} not found or could not be loaded`);
          }
          
          // Load adventure details if session has adventure information
          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch (adventureError) {
              console.warn('[GameStore] loadSessionState: Failed to load adventure details:', adventureError);
            }
          }
          
          if (import.meta.env.DEV) {
            console.log('[GameStore] loadSessionState: setting session/player state', {
              sessionId: session.id,
              playerId: player.id,
            });
          }
          set({
            currentSession: session,
            currentPlayer: player,
            currentAdventure: adventure,
            sessionPlayers: players,
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: session.adventureId || null,
            isLoading: false,
            error: null,
          });

          // Load current scene and options
          if (import.meta.env.DEV) {
            console.log('[GameStore] loadSessionState: calling loadCurrentScene');
          }
          await get().loadCurrentScene();
          if (import.meta.env.DEV) {
            console.log('[GameStore] loadSessionState: loadCurrentScene completed');
          }
          
        } catch (error) {
          console.error('[GameStore] loadSessionState: Error:', error);
          console.error('[GameStore] loadSessionState: Error details:', {
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            sessionId,
            playerId
          });
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      leaveSession: () => {
        const { currentSession, currentPlayer } = get();
        
        if (currentSession && currentPlayer) {
          // TODO: Implement WebSocket leave session
          // websocketService.leaveSession(currentSession.id, currentPlayer.id);
        }
        
        set({
          currentSession: null,
          currentPlayer: null,
          currentScene: null,
          availableOptionIds: [],
          waitingOptionIds: [],
          optionsMap: {},
          players: [],
          playerStates: {},
          waitingConditions: [],
          pendingUserInputs: {},
          error: null,
        });
      },

      selectOption: async (optionId: string) => {
        const { currentSession, currentPlayer } = get();
        
        if (!currentSession || !currentPlayer) {
          throw new Error('No active session or player');
        }
        
        set({ isLoading: true, error: null });
        
        try {
          const actionResponse = await apiService.performAction(
            currentSession.id,
            currentPlayer.id,
            { action: 'select_option', optionId }
          );
          
          // Handle START_ADVENTURE action - navigate to new adventure
          if (actionResponse.action === 'START_ADVENTURE' && actionResponse.adventureId) {
            if (import.meta.env.DEV) {
              console.log('[GameStore] START_ADVENTURE: received', {
                adventureId: actionResponse.adventureId,
                sessionId: actionResponse.sessionId,
                playerId: actionResponse.playerId,
              });
            }
            // Clear current session state
            get().clearSessionState();
            if (import.meta.env.DEV) {
              console.log('[GameStore] START_ADVENTURE: cleared session state, loading new session');
            }

            // If backend created the session and returned sessionId and playerId, we are the host — load that session state
            if (actionResponse.sessionId && actionResponse.playerId) {
              await get().loadSessionState(actionResponse.sessionId, actionResponse.playerId);
              if (import.meta.env.DEV) {
                const s = get();
                console.log('[GameStore] START_ADVENTURE: loadSessionState completed', {
                  currentSessionId: s.currentSession?.id,
                  currentPlayerId: s.currentPlayer?.id,
                  currentSceneId: s.currentScene?.id,
                });
              }
              // Return false since we're transitioning to a new adventure
              return false;
            } else {
              // Fallback: create a new session for the adventure
              const { sessionId } = await get().createSession(actionResponse.adventureId);
              
              // Join the session
              await get().joinSession(sessionId);
              
              return false;
            }
          }
          
          // Handle LOGGED_IN action - navigate to authenticated user's system adventure
          if (actionResponse.action === 'LOGGED_IN' && actionResponse.sessionId && actionResponse.playerId) {
            // Clear current session state (guest session)
            get().clearSessionState();
            
            // Verify tokens are set in authService (highest priority for axios interceptor)
            // The interceptor now checks authService first, so this is critical
            const authService = (await import('../services/auth')).default;
            const token = authService.getCurrentToken();
            
            if (!token) {
              console.error(`[GameStore] LOGGED_IN: AuthService does not have token! This will cause API calls to fail.`);
              // Try to get token from authStore as fallback (should have been set by waiting-update event)
              const { default: useAuthStore } = await import('./authStore');
              const authStoreState = useAuthStore.getState();
              if (authStoreState.token && !authStoreState.isGuest) {
                (authService as any).setTokens(authStoreState.token, authStoreState.refreshToken || '');
              } else {
                throw new Error('Authentication token not available after login');
              }
            }
            
            // No need for long delay - authService token is synchronous and interceptor checks it first
            // Just a small delay to ensure any pending state updates are processed
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Join the user's system adventure session
            try {
              await get().loadSessionState(actionResponse.sessionId, actionResponse.playerId);
              
            // Verify state was loaded
            const { currentSession, currentPlayer } = get();
            if (currentSession && currentPlayer) {
              // Navigate to the new session/player URL
              // Use window.location.href to ensure the URL is updated, which is critical for page reloads
              const newUrl = `/scene/${currentSession.id}/${currentPlayer.id}`;
              const currentUrl = window.location.pathname;
              
              if (currentUrl !== newUrl) {
                // Use window.location.href with replace to update the URL without adding to history
                // This ensures that if the page is reloaded, it will reload the new URL, not the old one
                window.history.replaceState({}, '', newUrl);
                // Also update the location to trigger React Router navigation
                window.location.href = newUrl;
              }
            } else {
              console.error(`[GameStore] LOGGED_IN: Session state not loaded - currentSession=${currentSession?.id}, currentPlayer=${currentPlayer?.id}`);
            }
          } catch (error) {
            console.error(`[GameStore] LOGGED_IN: Failed to load session state:`, error);
            // Re-throw to let ScenePage handle it
            throw error;
          }
          
          // Return false since we're transitioning to a new session
          return false;
          }
          
          // Handle JOIN_ADVENTURE action - navigate to game adventure from system adventure
          // Backend already added this user to the session; load that session state (do not call joinSession again)
          if (actionResponse.action === 'JOIN_ADVENTURE' && actionResponse.sessionId && actionResponse.playerId) {
            if (import.meta.env.DEV) {
              console.log('[GameStore] JOIN_ADVENTURE action received', {
                sessionId: actionResponse.sessionId,
                playerId: actionResponse.playerId,
              });
            }
            get().clearSessionState();
            await get().loadSessionState(actionResponse.sessionId, actionResponse.playerId);
            if (import.meta.env.DEV) {
              const state = get();
              console.log('[GameStore] JOIN_ADVENTURE loadSessionState done', {
                currentSessionId: state.currentSession?.id,
                currentPlayerId: state.currentPlayer?.id,
              });
            }
            return false;
          }
          
          // Merge character style into player if available
          const updatedPlayer = actionResponse.player ? (actionResponse.character ? {
            ...actionResponse.player,
            style: actionResponse.character.style
          } : actionResponse.player) : undefined;
          
          // Update player state
          if (updatedPlayer) {
            set({
              currentPlayer: updatedPlayer,
              isLoading: false,
              error: null,
            });
          } else {
            set({
              isLoading: false,
              error: null,
            });
          }
          
          // Update scene and options
          if (actionResponse.newScene) {
            const sceneState = prepareSceneState(
              {
                id: actionResponse.newScene.id,
                sections: actionResponse.newScene.sections,
                ...(actionResponse.newScene.style !== undefined && { style: actionResponse.newScene.style })
              },
              actionResponse.availableOptionIds || [],
              actionResponse.waitingOptionIds || [],
              get().userVariableValues
            );

            set({
              currentScene: sceneState.scene,
              availableOptionIds: sceneState.availableOptionIds,
              waitingOptionIds: sceneState.waitingOptionIds,
              optionsMap: sceneState.optionsMap,
              userVariableValues: sceneState.userVariableValues
            });
          }
          
          // Return completion status
          return updatedPlayer?.completed || false;
          
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      selectFinishOption: async () => {
        const { currentSession, currentPlayer } = get();
        
        if (!currentSession || !currentPlayer) {
          throw new Error('No active session or player');
        }
        
        set({ isLoading: true, error: null });
        
        try {
          await apiService.selectFinishOption(currentSession.id, currentPlayer.id);
          
          // Clear session state since the adventure is completed and session is closed
          set({
            currentSession: null,
            currentPlayer: null,
            currentScene: null,
            availableOptionIds: [],
            waitingOptionIds: [],
            optionsMap: {},
            sessionPlayers: [],
            sessionStatus: 'completed',
            selectedAdventure: null,
            selectedAdventureId: null,
            isLoading: false,
            error: null,
          });
          
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },


      updatePlayerState: (updates: Partial<Player>) => {
        const { currentPlayer } = get();
        
        if (currentPlayer) {
          const updatedPlayer = { ...currentPlayer, ...updates };
          set({ currentPlayer: updatedPlayer });
        }
      },

      setCurrentScene: (scene: { 
        id: string; 
        sections: SceneSection[];
        style?: CustomStyle;
      }) => {
        const { availableOptionIds, waitingOptionIds, userVariableValues } = get();
        const sceneState = prepareSceneState(
          scene,
          availableOptionIds,
          waitingOptionIds,
          userVariableValues
        );

        set({
          currentScene: sceneState.scene,
          optionsMap: sceneState.optionsMap,
          userVariableValues: sceneState.userVariableValues
        });
      },


      updateGameState: (state: any) => {
        set({
          players: state.players || [],
          playerStates: state.players?.reduce((acc: Record<string, any>, player: any) => {
            acc[player.id] = player;
            return acc;
          }, {}) || {},
        });
      },

      setConnectionStatus: (isConnected: boolean) => {
        set({ isConnected });
      },

      addWaitingCondition: (condition: WaitingCondition) => {
        const { waitingConditions } = get();
        set({
          waitingConditions: [...waitingConditions, condition],
        });
      },

      removeWaitingCondition: (playerId: string, optionId: string) => {
        const { waitingConditions } = get();
        set({
          waitingConditions: waitingConditions.filter(
            condition => !(condition.playerId === playerId && condition.optionId === optionId)
          ),
        });
      },

      setReconnectAttempts: (attempts: number) => {
        set({ reconnectAttempts: attempts });
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setUserVariableValue: (variableName: string, value: string, scope?: UserVariableScope) => {
        set((state) => {
          const previousScope = state.userVariableValues[variableName]?.scope;
          const entry = createVariableValueState(value, scope, previousScope);
          const nextMap: UserVariableMap = {
            ...state.userVariableValues,
            [variableName]: entry
          };

          return {
            userVariableValues: nextMap,
            currentScene: updateSceneWithVariables(state.currentScene, nextMap)
          };
        });
      },

      setUserVariableValues: (values: UserVariableMap) => {
        if (!values || Object.keys(values).length === 0) {
          return;
        }

        set((state) => {
          const sanitizedUpdates: UserVariableMap = {};
          Object.entries(values).forEach(([name, valueState]) => {
            const previousScope = state.userVariableValues[name]?.scope;
            sanitizedUpdates[name] = createVariableValueState(valueState.value, valueState.scope, previousScope);
          });

          const merged = mergeUserVariableMaps(state.userVariableValues, sanitizedUpdates);
          return {
            userVariableValues: merged,
            currentScene: updateSceneWithVariables(state.currentScene, merged)
          };
        });
      },

      setPendingUserInput: (
        variableName: string,
        value: string,
        options?: { maxLength?: number }
      ) => {
        set((state) => {
          const previousScope = state.userVariableValues[variableName]?.scope;
          const entry = createVariableValueState(
            value,
            state.userVariableValues[variableName]?.scope,
            previousScope
          );
          const nextUserVariableValues: UserVariableMap = {
            ...state.userVariableValues,
            [variableName]: entry
          };

          const nextPending = {
            ...state.pendingUserInputs,
            [variableName]: {
              value,
              ...(options?.maxLength !== undefined ? { maxLength: options.maxLength } : {})
            }
          };

          return {
            userVariableValues: nextUserVariableValues,
            currentScene: updateSceneWithVariables(state.currentScene, nextUserVariableValues),
            pendingUserInputs: nextPending
          };
        });
      },

      clearPendingUserInput: (variableName: string) => {
        set((state) => {
          if (!state.pendingUserInputs[variableName]) {
            return state;
          }

          const nextPending = { ...state.pendingUserInputs };
          delete nextPending[variableName];

          return {
            pendingUserInputs: nextPending
          };
        });
      },

      flushPendingUserInputs: async () => {
        const { pendingUserInputs } = get();
        const entries = Object.entries(pendingUserInputs);

        if (entries.length === 0) {
          return;
        }

        for (const [variableName, payload] of entries) {
          const lengthOptions =
            payload.maxLength !== undefined ? { maxLength: payload.maxLength } : undefined;
          await get().submitUserInput(variableName, payload.value, lengthOptions);
        }
      },

      // Adventure selection actions
      loadAdventures: async () => {
        set({ isLoadingAdventures: true, adventureError: null });
        
        try {
          const adventureIds = await apiService.listAdventures();
          const adventures = await Promise.all(
            adventureIds.map(id => apiService.getAdventureHeader(id))
          );
          
          set({
            adventureIds,
            availableAdventures: adventures,
            isLoadingAdventures: false,
            adventureError: null,
          });
        } catch (error) {
          set({
            isLoadingAdventures: false,
            adventureError: (error as ApiError).message || 'Failed to load adventures',
          });
          throw error;
        }
      },

      selectAdventure: (adventureId: string) => {
        const adventureIndex = get().adventureIds.indexOf(adventureId);
        const adventure = adventureIndex >= 0 ? get().availableAdventures[adventureIndex] : null;
        set({ 
          selectedAdventureId: adventureId,
          selectedAdventure: adventure || null
        });
      },

      clearAdventureError: () => {
        set({ adventureError: null });
      },

      // Adventure management
      loadAdventure: async (adventureId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // Load adventure header (includes style info) instead of full adventure
          const adventure = await apiService.getAdventureHeader(adventureId);
          set({
            currentAdventure: adventure,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      loadAdventureHeader: async (adventureId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const header = await apiService.getAdventureHeader(adventureId);
          set({
            selectedAdventure: header,
            selectedAdventureId: adventureId,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      loadAdventureScene: async (adventureId: string, sceneId: string) => {
        try {
          return await apiService.getAdventureScene(adventureId, sceneId);
        } catch (error) {
          set({ error: error as ApiError });
          throw error;
        }
      },

      loadAdventureItem: async (adventureId: string, itemName: string) => {
        try {
          return await apiService.getAdventureItem(adventureId, itemName);
        } catch (error) {
          set({ error: error as ApiError });
          throw error;
        }
      },

      loadAdventureStat: async (adventureId: string, statName: string) => {
        try {
          return await apiService.getAdventureStat(adventureId, statName);
        } catch (error) {
          set({ error: error as ApiError });
          throw error;
        }
      },

      createSession: async (adventureId: string) => {
        set({ isLoading: true, isCreatingSession: true, error: null, sessionError: null });
        
        try {
          // Get current user ID for createdBy field
          let authState = useAuthStore.getState();
          
          // Check if this is a system adventure and user is not authenticated
          // If so, use guest session instead
          if (adventureId === get().systemAdventureId && !authState.isAuthenticated) {
            return await get().createGuestSession(adventureId);
          }
          
          // If authenticated but user object is not set, try to fetch it
          if (authState.isAuthenticated && !authState.user?.id && !authState.isGuest) {
            try {
              // Try to get the user from the API
              const user = await authService.getCurrentUser();
              if (user?.id) {
                // Update auth store with user
                useAuthStore.getState().setUser(user);
                // Refresh auth state
                authState = useAuthStore.getState();
              }
            } catch (error) {
              console.warn('Failed to fetch user, continuing with existing auth state', error);
            }
          }
          
          if (!authState.isAuthenticated) {
            throw new Error('User must be logged in to create a session');
          }
          
          // For guest users, use guestUserId; for regular users, use user.id
          const userId = authState.isGuest ? authState.guestUserId : authState.user?.id;
          
          if (!userId) {
            throw new Error('User ID is required to create a session. Please ensure you are logged in.');
          }
          
          const { session, players } = await apiService.createSession({
            adventureId,
            playerIds: [], // Will be populated when players join
            createdBy: userId,
          });
          
          // Load adventure details
          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch (adventureError) {
              console.warn('Failed to load adventure details:', adventureError);
            }
          }
          
          // Set current player (should be the first player created - host)
          const currentPlayer = players.length > 0 ? players[0] : null;
          
          // Clear session state and set new session
          get().clearSessionState();
          set({
            currentSession: session,
            currentPlayer: currentPlayer,
            currentAdventure: adventure,
            sessionPlayers: players,
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: adventureId,
            isCreatingSession: false,
            isLoading: false,
            error: null,
          });
          
          // Automatically start the session to initialize location inventories
          try {
            await apiService.startSession(session.id);
            // Update session status to active
            set({
              sessionStatus: 'active',
            });
          } catch (startError) {
            console.warn('Failed to start session automatically:', startError);
            // Don't throw error - session was created successfully, just not started
          }
          
          // Load current scene and options if player was created
          if (currentPlayer) {
            await get().loadCurrentScene();
          }
          
          if (currentPlayer?.id) {
            return { sessionId: session.id, playerId: currentPlayer.id };
          } else {
            return { sessionId: session.id };
          }
        } catch (error) {
          console.error('[GameStore] createGuestSession failed:', error);
          if (error instanceof Error) {
            console.error('[GameStore] Error message:', error.message);
            console.error('[GameStore] Error stack:', error.stack);
          }
          set({
            isLoading: false,
            isCreatingSession: false,
            error: error as ApiError,
            sessionError: (error as ApiError).message || 'Failed to create session',
          });
          throw error;
        }
      },

      createGuestSession: async (
        adventureId: string,
        invitePayload?: { variables?: Record<string, string> }
      ) => {
        set({ isLoading: true, isCreatingSession: true, error: null, sessionError: null });
        
        let response: Awaited<ReturnType<typeof apiService.createGuestSession>>;
        try {
          // Get existing guest user ID if available (may be stale if backend restarted or guest was purged)
          const existingGuestData = guestService.getGuestData();
          const guestUserId = existingGuestData?.userId;

          // Create guest session (or restore existing guest)
          response = await apiService.createGuestSession(adventureId, guestUserId, invitePayload);
        } catch (firstError) {
          // If backend no longer has this guest (e.g. server restarted, guest purged, or guest was converted to registered user), clear and retry as new guest
          const apiErr = firstError as ApiError;
          const isStaleGuest =
            apiErr?.code === 'USER_NOT_FOUND' ||
            (apiErr?.code === 'VALIDATION_ERROR' &&
              typeof apiErr?.message === 'string' &&
              apiErr.message.includes('not a guest user'));
          if (isStaleGuest) {
            useAuthStore.getState().clearGuestData();
            guestService.clearGuestData();
            response = await apiService.createGuestSession(adventureId, undefined, invitePayload);
          } else {
            set({
              isLoading: false,
              isCreatingSession: false,
              error: firstError as ApiError,
              sessionError: (firstError as ApiError).message || 'Failed to create guest session',
            });
            throw firstError;
          }
        }

        try {
          const { session, player, token, refreshToken, guestUserId: returnedGuestUserId } = response;

          // Store guest tokens in guestService
          guestService.clearGuestData();
          localStorage.setItem('guestUserId', returnedGuestUserId);
          localStorage.setItem('guestToken', token);
          localStorage.setItem('guestRefreshToken', refreshToken);
          
          // Set guest tokens in auth store for API calls
          // IMPORTANT: This must be called BEFORE any navigation to ensure ProtectedRoute allows access
          useAuthStore.getState().setGuestTokens(token, refreshToken, returnedGuestUserId);
          
          // Wait a moment for state to propagate to ensure ProtectedRoute sees isAuthenticated: true
          await new Promise(resolve => setTimeout(resolve, 50));

          // Load adventure details
          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch (adventureError) {
              // Adventure loading failed, continue without it
            }
          }

          // Clear session state and set new session
          get().clearSessionState();
          set({
            currentSession: session,
            currentPlayer: player,
            currentAdventure: adventure,
            sessionPlayers: player ? [player] : [],
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: adventureId,
            isCreatingSession: false,
            isLoading: false,
            error: null,
          });

          // Sessions are active from creation, no need to start
          // Load current scene and options if player was created
          // NOTE: We load the scene here so it's ready when we navigate to ScenePage
          if (player) {
            try {
              await get().loadCurrentScene();
              // Ensure isLoading is false after scene is loaded
              if (get().isLoading) {
                set({ isLoading: false });
              }
            } catch (sceneError) {
              // Ensure isLoading is false even on error
              set({ isLoading: false });
              // Don't throw - we can still return the session/player IDs
              // The ScenePage will try to load the scene again if needed
            }
          }

          const result = player?.id 
            ? { sessionId: session.id, playerId: player.id }
            : { sessionId: session.id };
          
          return result;
        } catch (error) {
          set({
            isLoading: false,
            isCreatingSession: false,
            error: error as ApiError,
            sessionError: (error as ApiError).message || 'Failed to create guest session',
          });
          throw error;
        }
      },

      startSession: async () => {
        const { currentSession } = get();
        
        if (!currentSession) {
          throw new Error('No active session');
        }
        
        set({ isLoading: true, error: null });
        
        try {
          await apiService.startSession(currentSession.id);
          
          // Update session status
          set({
            currentSession: {
              ...currentSession,
              status: 'active' as GameStatus,
            } as GameSession,
            isLoading: false,
            error: null,
          });
          
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      // Helper method to load current scene
      loadCurrentScene: async () => {
        const { currentSession, currentPlayer } = get();

        if (!currentSession || !currentPlayer) {
          console.warn('[GameStore] loadCurrentScene: Missing session or player', {
            hasSession: !!currentSession,
            hasPlayer: !!currentPlayer,
          });
          if (get().isLoading) {
            set({ isLoading: false });
          }
          return;
        }

        const key = `${currentSession.id}:${currentPlayer.id}`;
        if (loadCurrentScenePromise && loadCurrentSceneKey === key) {
          return loadCurrentScenePromise;
        }

        const runLoad = async (): Promise<void> => {
          const state = get();
          const session = state.currentSession;
          const player = state.currentPlayer;
          if (!session || !player) return;

          try {
            console.log('[Scene] Loading scene...', {
              sessionId: session.id,
              playerId: player.id,
            });
            const sceneResponse = await apiService.getCurrentScene(
              session.id,
              player.id
            );

            // Merge player data from response to capture dynamic fields like portraitId
            const playerWithResponseData = sceneResponse.player
              ? { ...player, ...sceneResponse.player }
              : player;

          // Merge character style into current player if available
          const updatedPlayer = sceneResponse.character?.style
            ? {
                ...playerWithResponseData,
                style: sceneResponse.character.style,
              }
            : playerWithResponseData;

          const sceneState = prepareSceneState(
            {
              id: sceneResponse.scene.id,
              sections: sceneResponse.scene.sections,
              ...(sceneResponse.scene.style !== undefined && { style: sceneResponse.scene.style })
            },
            sceneResponse.availableOptionIds || [],
            sceneResponse.waitingOptionIds || [],
            get().userVariableValues
          );

          set({
            currentPlayer: updatedPlayer,
            currentScene: sceneState.scene,
            availableOptionIds: sceneState.availableOptionIds,
            waitingOptionIds: sceneState.waitingOptionIds,
            optionsMap: sceneState.optionsMap,
            userVariableValues: sceneState.userVariableValues,
            isLoading: false, // IMPORTANT: Set isLoading to false after scene is loaded
            error: null,
          });

          const sceneId = sceneState.scene?.id;
          const firstSectionDesc = sceneState.scene?.sections?.[0]?.description;
          const sceneName =
            typeof firstSectionDesc === 'string'
              ? firstSectionDesc.slice(0, 80)
              : undefined;
          console.log('[Scene] Scene loaded', {
            sceneId,
            ...(sceneName && { sceneName }),
          });

          // Check if guest user has completed login and needs token exchange
          // This happens when a guest user logs in via async operation
          // We attempt token exchange whenever we're a guest - the backend will tell us
          // if migration hasn't completed yet, and we'll retry on the next scene load
          // Only attempt exchange once per scene load to avoid infinite loops
          const authStore = (await import('./authStore')).default.getState();
          if (authStore.isGuest && authStore.token) {
            // Check if we've already attempted exchange for this scene
            // Use a simple flag to prevent repeated attempts
            const exchangeKey = `exchange_attempted_${sceneState.scene?.id}`;
            const lastExchangeAttempt = (globalThis as any)[exchangeKey];
            const now = Date.now();
            // Use 10s backoff for migration-not-complete case; 5s for other retries
            const backoffMs = 10000;
            
            if (!lastExchangeAttempt || (now - lastExchangeAttempt) > backoffMs) {
              (globalThis as any)[exchangeKey] = now;
              
              // Try to exchange guest token for user tokens
              // This is safe to call repeatedly - the backend will only succeed if migration has completed
              try {
                await authStore.exchangeGuestToken();
                // Clear the flag on success
                delete (globalThis as any)[exchangeKey];
                
                // Only reload scene when in system adventure - skip for content adventures (e.g. JOIN_ADVENTURE)
                // to avoid racing with session load and causing "Missing session or player"
                const gameState = get();
                if (gameState.currentAdventure?.id === gameState.systemAdventureId) {
                  // After successful token exchange, reload the current scene to ensure
                  // all data is refreshed with the new user context
                  setTimeout(async () => {
                    try {
                      await get().loadCurrentScene();
                    } catch (error) {
                      console.warn('[GameStore] Failed to reload scene after token exchange:', error);
                    }
                  }, 100);
                }
              } catch (error: any) {
                // If exchange fails, it's because migration hasn't completed yet
                // This is expected and we'll try again on next scene load
                if (error?.code === 'VALIDATION_ERROR' && error?.message?.includes('not been migrated')) {
                  // Migration not complete yet - this is expected, will retry later
                  // Don't log as warning since this is normal during the migration process
                } else {
                  // Other errors (network, etc.) - log but don't fail
                  console.warn('[GameStore] Failed to exchange guest token (will retry):', error?.message || error);
                }
              }
            }
          }
          
          } catch (error) {
            set({
              isLoading: false,
              error: error as ApiError,
            });
            throw error;
          } finally {
            loadCurrentScenePromise = null;
            loadCurrentSceneKey = null;
          }
        };

        loadCurrentSceneKey = key;
        loadCurrentScenePromise = runLoad();
        return loadCurrentScenePromise;
      },

      // Session lobby actions
      joinSessionByCode: async (sessionCode: string) => {
        set({ isLoading: true, sessionError: null });
        
        try {
          const session = await apiService.getSession(sessionCode);
          const players = await apiService.getSessionPlayers(sessionCode);
          
          // Load adventure details if session has adventure information
          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch (adventureError) {
              console.warn('Failed to load adventure details:', adventureError);
              // Continue without adventure details - session will still work
            }
          }
          
          

          // Clear session state and set new session data
          get().clearSessionState();
          set({
            currentSession: session,
            sessionPlayers: players,
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: session.adventureId || null,
          });
        } catch (error) {
          set({
            isLoading: false,
            sessionError: (error as ApiError).message || 'Failed to join session',
          });
          throw error;
        }
      },

      clearSessionError: () => {
        set({ sessionError: null });
      },

      loadSessionPlayers: async (sessionId: string) => {
        try {
          const players = await apiService.getSessionPlayers(sessionId);
          set({ sessionPlayers: players });
        } catch (error) {
          set({ sessionError: (error as ApiError).message || 'Failed to load players' });
        }
      },



      // Helper actions
      loadSystemSession: async () => {
        set({ isLoading: true, error: null });
        
        try {
          const { session, player } = await apiService.getSystemSession();
          
          let adventure = null;
          if (session.adventureId) {
            try {
              adventure = await apiService.getAdventureHeader(session.adventureId);
            } catch (adventureError) {
              console.warn('Failed to load adventure details:', adventureError);
            }
          }
          
          let currentScene = null;
          let availableOptionIds: string[] = [];
          let waitingOptionIds: string[] = [];
          let updatedPlayer: any = player;
          
          try {
            const sceneResponse = await apiService.getCurrentScene(session.id, player.id);
            
            updatedPlayer = sceneResponse.character ? {
              ...player,
              style: sceneResponse.character.style
            } : player;
            
            const sceneState = prepareSceneState(
              {
                id: sceneResponse.scene.id,
                sections: sceneResponse.scene.sections,
                ...(sceneResponse.scene.style !== undefined && { style: sceneResponse.scene.style })
              },
              sceneResponse.availableOptionIds || [],
              sceneResponse.waitingOptionIds || [],
              get().userVariableValues
            );

            currentScene = sceneState.scene;
            availableOptionIds = sceneState.availableOptionIds;
            waitingOptionIds = sceneState.waitingOptionIds;

            set({
              currentSession: session,
              currentPlayer: updatedPlayer,
              currentAdventure: adventure,
              currentScene: sceneState.scene,
              sessionPlayers: [updatedPlayer],
              sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
              selectedAdventure: adventure,
              selectedAdventureId: session.adventureId || null,
              availableOptionIds: sceneState.availableOptionIds,
              waitingOptionIds: sceneState.waitingOptionIds,
              optionsMap: sceneState.optionsMap,
              userVariableValues: sceneState.userVariableValues,
              isLoading: false,
              error: null,
            });

            return { sessionId: session.id, playerId: player.id };
          } catch (sceneError) {
            console.warn('Failed to load current scene:', sceneError);
          }
          
          set({
            currentSession: session,
            currentPlayer: updatedPlayer,
            currentAdventure: adventure,
            currentScene,
            sessionPlayers: [updatedPlayer],
            sessionStatus: session.status as 'waiting' | 'active' | 'completed' | 'paused',
            selectedAdventure: adventure,
            selectedAdventureId: session.adventureId || null,
            availableOptionIds,
            waitingOptionIds,
            isLoading: false,
            error: null,
          });
          
          return { sessionId: session.id, playerId: player.id };
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      clearSessionState: () => {
        if (import.meta.env.DEV) {
          const stack = new Error().stack;
          console.log('[GameStore] clearSessionState called', {
            caller: stack?.split('\n').slice(1, 4).join(' <- '),
          });
        }
        set({
          currentSession: null,
          currentPlayer: null,
          currentAdventure: null,
          currentScene: null,
          sessionPlayers: [],
          sessionStatus: 'waiting',
          selectedAdventure: null,
          selectedAdventureId: null,
          players: [],
          playerStates: {},
          locationInventories: {},
          availableOptionIds: [],
          waitingOptionIds: [],
          optionsMap: {},
          waitingConditions: [],
          pendingUserInputs: {},
          userVariableValues: {},
          isLoading: false,
          error: null,
          sessionError: null,
        });
      },

      // Session management actions
      deleteSession: async (sessionId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          await apiService.deleteSession(sessionId);
          
          // Clear current session data
          get().clearSessionState();
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      leaveSessionById: async (sessionId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          await apiService.leaveSession(sessionId);
          
          // Clear current session data
          get().clearSessionState();
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError,
          });
          throw error;
        }
      },

      submitUserInput: async (variableName: string, value: string, options?: { maxLength?: number }) => {
        const { currentSession, currentPlayer, userVariableValues } = get();

        if (!currentSession || !currentPlayer) {
          throw new Error('No active session or player');
        }

        try {
          const payload: { variableName: string; value?: string; maxLength?: number } = {
            variableName,
            value
          };

          if (options?.maxLength !== undefined) {
            payload.maxLength = options.maxLength;
          }

          const response = await apiService.submitUserInput(currentSession.id, currentPlayer.id, payload);

          const updatedPlayer = response.player
            ? { ...currentPlayer, ...response.player }
            : currentPlayer;

          const variableUpdate: UserVariableMap = {
            [variableName]: createVariableValueState(
              response.value ?? value ?? '',
              response.scope,
              userVariableValues[variableName]?.scope
            )
          };

          const mergedVariables = mergeUserVariableMaps(userVariableValues, variableUpdate);

          const sceneState = response.scene
            ? prepareSceneState(
                {
                  id: response.scene.id,
                  sections: response.scene.sections,
                  ...(response.scene.style !== undefined && { style: response.scene.style })
                },
                response.availableOptionIds || [],
                response.waitingOptionIds || [],
                mergedVariables
              )
            : {
                scene: updateSceneWithVariables(get().currentScene, mergedVariables),
                availableOptionIds: get().availableOptionIds,
                waitingOptionIds: get().waitingOptionIds,
                optionsMap: get().optionsMap,
                userVariableValues: mergedVariables
              };

          const pendingInputs = get().pendingUserInputs;
          const nextPendingInputs = { ...pendingInputs };
          delete nextPendingInputs[variableName];

          set({
            currentPlayer: updatedPlayer,
            currentScene: sceneState.scene,
            availableOptionIds: sceneState.availableOptionIds,
            waitingOptionIds: sceneState.waitingOptionIds,
            optionsMap: sceneState.optionsMap,
            userVariableValues: sceneState.userVariableValues,
            pendingUserInputs: nextPendingInputs,
            isLoading: false,
            error: null
          });

          return response;
        } catch (error) {
          set({
            isLoading: false,
            error: error as ApiError
          });
          throw error;
        }
      },

      // Location inventory actions
      updateLocationInventory: (locationTag: string, items: string[]) => {
        const { locationInventories } = get();
        set({
          locationInventories: {
            ...locationInventories,
            [locationTag]: items
          }
        });
      },

      addItemToLocation: (locationTag: string, itemTag: string) => {
        const { locationInventories } = get();
        const currentItems = locationInventories[locationTag] || [];
        if (!currentItems.includes(itemTag)) {
          set({
            locationInventories: {
              ...locationInventories,
              [locationTag]: [...currentItems, itemTag]
            }
          });
        }
      },

      removeItemFromLocation: (locationTag: string, itemTag: string) => {
        const { locationInventories } = get();
        const currentItems = locationInventories[locationTag] || [];
        set({
          locationInventories: {
            ...locationInventories,
            [locationTag]: currentItems.filter(item => item !== itemTag)
          }
        });
      },

      getLocationInventory: (locationTag: string) => {
        const { locationInventories } = get();
        return locationInventories[locationTag] || [];
      },
    }),
    {
      name: 'game-storage',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentSession: state.currentSession,
        currentPlayer: state.currentPlayer,
        currentAdventure: state.currentAdventure,
        currentScene: state.currentScene,
        availableOptionIds: state.availableOptionIds,
        waitingOptionIds: state.waitingOptionIds,
        players: state.players,
        playerStates: state.playerStates,
        userVariableValues: state.userVariableValues,
      }),
      migrate: (persistedState: any, version: number) => {
        // Handle migration from version 0 to 1 (adding availableOptionIds and waitingOptionIds)
        if (version === 0) {
          return {
            ...persistedState,
            availableOptionIds: [],
            waitingOptionIds: [],
          };
        }
        return persistedState;
      },
    }
  )
);

export default useGameStore;
